/**
 * Contact → form-state reset/sync, extracted verbatim from the big init
 * `useEffect` in `ContactEditModal.tsx`. The orchestrator keeps the effect
 * shell and its EXACT `[contact, profile?.id, profile?.name]` dependency
 * array, delegating the body here. Behavior is unchanged (#3237 Pass-2).
 *
 * This is the highest-risk reset path (the spec's "stale-field" concern):
 * every field is re-seeded from the contact (with the API's flattened-
 * attributes fallbacks) on open, or cleared to new-contact defaults. The
 * deps object exposes the live setters + the `optimisticSendRef` (passed by
 * reference so the optimistic-send guard reads/writes the same ref) and the
 * `profile`/`initialStage` values captured at effect-run time.
 */

import { DEFAULT_LABEL_COLOR } from '../../../shared/colors';
import { notesToActivities } from './contactMapping';
import type { EmailAttachment } from './emailAttachments';
import type { Activity, ContactNote, ContactObject } from './types';

export interface ContactFormInitDeps {
  profile: { id?: string; name?: string } | null | undefined;
  initialStage: string | undefined;
  optimisticSendRef: { current: boolean };
  setIsViewMode: (b: boolean) => void;
  setFreeformMode: (b: boolean) => void;
  setFreeformText: (s: string) => void;
  setFreeformParsed: (p: Record<string, any> | null) => void;
  setEmailAttachments: (a: EmailAttachment[]) => void;
  setFirstName: (s: string) => void;
  setLastName: (s: string) => void;
  setName: (s: string) => void;
  setDisplayNameOverride: (s: string | null) => void;
  setEmail: (s: string) => void;
  setAdditionalEmails: (s: string[]) => void;
  setPhone: (s: string) => void;
  setCompany: (s: string) => void;
  setLists: (s: string[]) => void;
  setStage: (s: string) => void;
  setXHandle: (s: string) => void;
  setSummary: (s: string) => void;
  setDealValue: (s: string) => void;
  setProbability: (s: string) => void;
  setActivities: (a: Activity[]) => void;
  setLabel: (s: string) => void;
  setLabelColor: (s: string) => void;
  setNextStepAction: (s: string) => void;
  setNextStepDueAt: (s: string) => void;
  setLastSavedFollowUp: (f: { action: string; dueAt: string } | null) => void;
  setLinkedinUrl: (s: string) => void;
  setContactTitle: (s: string) => void;
  setFirm: (s: string) => void;
  setLocation: (s: string) => void;
  setEstimatedAum: (s: string) => void;
  setAccreditedStatus: (s: string) => void;
  setContactSource: (s: string) => void;
  setEnrichmentTags: (s: string[]) => void;
  setEnrichmentSummary: (s: string) => void;
  setEnrichmentSkills: (s: string[]) => void;
  setEnrichmentConfidence: (s: string) => void;
  setEnrichmentAttemptedAt: (s: string) => void;
  setDoNotBulkEnrichLocal: (b: boolean) => void;
  setEnrichmentReviewedAtLocal: (s: string) => void;
  setEnrichResult: (r: null) => void;
  setEnrichError: (s: string | null) => void;
  setEnrichLoading: (b: boolean) => void;
  setEnrichUnsaved: (b: boolean) => void;
  setEnrichHintsPrompt: (b: boolean) => void;
  setEnrichHints: (s: string) => void;
  setNewNote: (s: string) => void;
  setCustomLabelInput: (s: string) => void;
  setIsEditingFollowUp: (b: boolean) => void;
}

export function syncContactToForm(contact: ContactObject | null | undefined, deps: ContactFormInitDeps): void {
  const {
    profile, initialStage, optimisticSendRef,
    setIsViewMode, setFreeformMode, setFreeformText, setFreeformParsed, setEmailAttachments,
    setFirstName, setLastName, setName, setDisplayNameOverride, setEmail, setAdditionalEmails, setPhone, setCompany,
    setLists, setStage, setXHandle, setSummary, setDealValue, setProbability, setActivities,
    setLabel, setLabelColor, setNextStepAction, setNextStepDueAt, setLastSavedFollowUp,
    setLinkedinUrl, setContactTitle, setFirm, setLocation, setEstimatedAum, setAccreditedStatus,
    setContactSource, setEnrichmentTags, setEnrichmentSummary, setEnrichmentSkills,
    setEnrichmentConfidence, setEnrichmentAttemptedAt, setDoNotBulkEnrichLocal,
    setEnrichmentReviewedAtLocal, setEnrichResult, setEnrichError, setEnrichLoading,
    setEnrichUnsaved, setEnrichHintsPrompt, setEnrichHints, setNewNote, setCustomLabelInput,
    setIsEditingFollowUp,
  } = deps;

  if (contact) {
    setIsViewMode(true);
    setFreeformMode(false);
    setFreeformText('');
    setFreeformParsed(null);
    setEmailAttachments([]);
    optimisticSendRef.current = false;
    const attrs = contact.attributes || {};
    const displayName = contact.name || contact.title || attrs.name || '';

    // Parse name for first/last
    const nameParts = displayName.split(' ');
    setFirstName(nameParts[0] || '');
    setLastName(nameParts.slice(1).join(' ') || '');

    setName(displayName);
    // Load display-name override (checked at render time by formatPersonName)
    setDisplayNameOverride(
      contact.displayNameOverride ?? (attrs.displayNameOverride as string | null | undefined) ?? null,
    );
    // Normalize all emails (tolerating string[] / {value}[] / singular shapes),
    // dedupe, then split into primary + the rest for the multi-email editor.
    const _emailRaw = [
      ...(Array.isArray(contact.emails) ? contact.emails : []),
      ...(Array.isArray(attrs.emails) ? (attrs.emails as unknown[]) : []),
      contact.email, attrs.email as unknown,
    ];
    const _emails = [...new Set(
      _emailRaw
        .map((e) => (typeof e === 'string' ? e : (e && typeof e === 'object' && typeof (e as { value?: unknown }).value === 'string' ? (e as { value: string }).value : '')))
        .map((s) => s.trim())
        .filter(Boolean),
    )];
    setEmail(_emails[0] || '');
    setAdditionalEmails(_emails.slice(1));
    // Phones mirror the email hardening above: synced contacts carry
    // `phones` as the canonical `{ value }[]`, so a raw `phones[0]` is an
    // object — feeding it to setPhone renders `[object Object]` and the save
    // throws (#3756 F12). Extract the value from each shape, first non-empty.
    const _phoneRaw = [
      ...(Array.isArray(contact.phones) ? contact.phones : []),
      ...(Array.isArray(attrs.phones) ? (attrs.phones as unknown[]) : []),
      contact.phone, attrs.phone as unknown,
    ];
    const _phone = _phoneRaw
      .map((p) => (typeof p === 'string' ? p : (p && typeof p === 'object' && typeof (p as { value?: unknown }).value === 'string' ? (p as { value: string }).value : '')))
      .map((s) => s.trim())
      .find(Boolean) || '';
    setPhone(_phone);
    setCompany(contact.organization || contact.company || attrs.organization || '');
    setLists(Array.isArray(contact.lists) ? contact.lists : (Array.isArray(attrs.lists) ? attrs.lists : []));
    setStage(contact.stage || 'lead');
    setXHandle(contact.xHandle || attrs.xHandle || '');
    setSummary(contact.summary || attrs.summary || '');

    // CRM fields
    setDealValue(contact.dealValue ? contact.dealValue.toLocaleString('en-US') : (attrs.dealValue ? attrs.dealValue.toLocaleString('en-US') : ''));
    setProbability(contact.probability?.toString() || attrs.probability?.toString() || '');
    // Load notes (canonical format) first, fall back to legacy activities
    const rawNotes = contact.notes || attrs.notes || [];
    const contactNotes: ContactNote[] = Array.isArray(rawNotes) ? rawNotes : [];
    const legacyActivities: Activity[] = contact.activities || attrs.activities || [];
    // Migrate notes to ensure they have authorId/authorName
    const migratedNotes = contactNotes.map(note => ({
      ...note,
      authorId: note.authorId || profile?.id || '',
      authorName: note.authorName || profile?.name || 'Unknown',
    }));
    // Don't overwrite activities during an optimistic send — let the local state persist
    if (!optimisticSendRef.current) {
      setActivities(migratedNotes.length > 0 ? notesToActivities(migratedNotes) : legacyActivities);
    }
    setLabel(contact.label || attrs.label || '');
    setLabelColor(contact.labelColor || attrs.labelColor || DEFAULT_LABEL_COLOR);
    const action = contact.nextStep?.action || attrs.nextStep?.action || '';
    const dueAt = contact.nextStep?.dueAt ? new Date(contact.nextStep.dueAt).toISOString().split('T')[0] : '';
    setNextStepAction(action);
    setNextStepDueAt(dueAt);
    // Track initial follow-up state to detect changes
    setLastSavedFollowUp(dueAt ? { action, dueAt } : null);

    // Enrichment fields
    setLinkedinUrl(contact.linkedinUrl || attrs.linkedinUrl || '');
    // Canonical jobTitle/organization first (the backfill folded the legacy
    // contactTitle/firm); keep the legacy attribute as a fallback only for the
    // brief window before FE query caches expire. (Fixes: backfilled contacts
    // showed an empty Job Title because this read only the folded-away contactTitle.)
    // If jobTitle was erroneously set to the contact's own display name (a known
    // data-quality issue from enrichment), omit it so the field appears blank
    // rather than echoing the name.
    const rawJobTitle = (attrs.jobTitle || contact.contactTitle || attrs.contactTitle || '') as string;
    const titleIsName = rawJobTitle.trim() !== '' && displayName.trim() !== ''
      && rawJobTitle.trim().toLowerCase() === displayName.trim().toLowerCase();
    setContactTitle(titleIsName ? '' : rawJobTitle);
    setFirm(attrs.organization || contact.firm || attrs.firm || '');
    setLocation(contact.location || attrs.location || '');
    setEstimatedAum(attrs.estimatedAum ? String(attrs.estimatedAum) : '');
    setAccreditedStatus(attrs.accreditedInvestorStatus || 'unknown');
    setContactSource(attrs.source || '');
    setEnrichmentTags(Array.isArray(attrs.enrichmentTags) ? attrs.enrichmentTags : []);
    // Load persisted enrichment data (API flattens attributes to top level)
    setEnrichmentSummary(contact.enrichmentSummary || attrs.enrichmentSummary || '');
    setEnrichmentSkills(Array.isArray(contact.enrichmentSkills) ? contact.enrichmentSkills : Array.isArray(attrs.enrichmentSkills) ? attrs.enrichmentSkills : []);
    setEnrichmentConfidence(contact.enrichmentConfidence || attrs.enrichmentConfidence || '');
    setEnrichmentAttemptedAt(contact.enrichmentAttemptedAt || attrs.enrichmentAttemptedAt || '');
    // API flattens attributes to top-level; check both shapes.
    const cTop = contact as Record<string, unknown>;
    setDoNotBulkEnrichLocal((cTop.doNotBulkEnrich === true) || attrs.doNotBulkEnrich === true);
    const reviewedTop = typeof cTop.enrichmentReviewedAt === 'string' ? cTop.enrichmentReviewedAt as string : '';
    const reviewedNested = typeof attrs.enrichmentReviewedAt === 'string' ? attrs.enrichmentReviewedAt as string : '';
    setEnrichmentReviewedAtLocal(reviewedTop || reviewedNested);
    setEnrichResult(null);
    setEnrichError(null);
    setEnrichLoading(false);
    setEnrichUnsaved(false);
    setEnrichHintsPrompt(false);
    setEnrichHints(contact.enrichHints || attrs.enrichHints || '');
  } else {
    setIsViewMode(false);
    setFreeformMode(true);
    setFreeformText('');
    setFreeformParsed(null);
    setFirstName('');
    setLastName('');
    setName('');
    setEmail('');
    setAdditionalEmails([]);
    setPhone('');
    setCompany('');
    setLists([]);
    setDealValue('');
    setProbability('');
    setActivities([]);
    setEmailAttachments([]);
    setLabel('');
    setLabelColor(DEFAULT_LABEL_COLOR);
    setNextStepAction('Follow Up');
    // Default to 1 week from now for new contacts
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
    const defaultDueAt = oneWeekFromNow.toISOString().split('T')[0];
    setNextStepDueAt(defaultDueAt);
    // New contacts start with no saved follow-up
    setLastSavedFollowUp(null);
    setStage(initialStage || 'lead');
    setXHandle('');
    setSummary('');
    setLinkedinUrl('');
    setContactTitle('');
    setFirm('');
    setLocation('');
    setEstimatedAum('');
    setAccreditedStatus('unknown');
    setContactSource('');
    setEnrichmentTags([]);
    setEnrichmentSummary('');
    setEnrichmentSkills([]);
    setEnrichmentConfidence('');
    setEnrichResult(null);
    setEnrichError(null);
    setEnrichLoading(false);
    setEnrichUnsaved(false);
    setEnrichHintsPrompt(false);
    setEnrichHints('');
  }
  setNewNote('');
  setCustomLabelInput('');
  setIsEditingFollowUp(false);
}
